// The Support One-Pager model (progress-views design §7) — pure, no I/O.
//
// Built from one child's synced skill-step snapshot, their session summaries and
// their support profile, over a period (default: the last 13 weeks, ending
// today). Everything here is OBSERVATION of play at home: counts of days and
// steps, the stages the app's own stage function reached, and dates. There is no
// score, no comparison with other children and no expectation for an age — the
// area "dumbbells" compare a child only with their own start of the period.
//
// The model carries keys and data, not sentences; the screen and the PDF turn
// it into EN / MS text from the dictionary. The skill label and the step's
// can-do sentence come from the generated registry, in the requested language
// (falling back to English).

import type { HomeNote, IsoDate, SessionSummary, SkillStepState, StepStage, SupportProfile } from "../domain/types";
import { addDays, daysBetween, localDay, mondayOf } from "./weeks";
import { currentStage, effectiveStageDates, isOwnOrAbove, stageOn, STAGES } from "./stages";
import { domainRank, localized, skillById, skillRank } from "./registry";

/** Inclusive calendar-day period. */
export interface Period {
  from: IsoDate;
  to: IsoDate;
}

export const PERIOD_WEEKS = 13;

/** The last 13 weeks, ending on `now`'s local day. */
export function defaultPeriod(now: Date = new Date()): Period {
  const to = localDay(now);
  return { from: addDays(to, -(PERIOD_WEEKS * 7 - 1)), to };
}

export type SupportKey = "captions" | "reducedMotion" | "highContrast" | "calmMode" | "tapOnly";

export const SUPPORT_KEYS: readonly SupportKey[] = [
  "captions",
  "reducedMotion",
  "highContrast",
  "calmMode",
  "tapOnly",
];

export interface SupportSummary {
  /** The settings that are ON, in a fixed order. */
  settings: SupportKey[];
  /** The app language code, or null when not shared. */
  language: string | null;
  /** False when no support profile has synced at all. */
  shared: boolean;
}

export interface PracticeSummary {
  /** Distinct local days with at least one session in the period. */
  daysPlayed: number;
  /** Distinct weeks (Monday-based) with at least one session in the period. */
  activeWeeks: number;
  /** Weeks the period spans. */
  weeksInPeriod: number;
  /** Steps that reached any new stage inside the period. */
  stepsMovedUp: number;
  /** Steps currently Exploring or With a little help. */
  stepsInProgress: number;
}

export interface StepRef {
  skillId: string;
  skillLabel: string;
  domain: string;
  step: number;
  /** The step's can-do sentence from the registry. */
  canDo: string;
  lastPractisedOn: IsoDate | null;
}

export interface NewStep extends StepRef {
  /** The highest stage reached inside the period: own, steady or places. */
  stage: StepStage;
  reachedOn: IsoDate;
}

export interface WorkingStep extends StepRef {
  /** When the step reached "With a little help". */
  since: IsoDate | null;
}

export interface AreaDumbbell {
  domain: string;
  /** Steps with no game help (own or above) at the start of the period. */
  start: number;
  /** …and at the end. Never lower than `start`: stages never go down. */
  end: number;
}

/** A "Seen at home" note the family shared, inside the period (design §7b). */
export interface SeenAtHome {
  id: string;
  skillId: string;
  /** The skill's name in the requested language; the id when the registry does not know it. */
  skillLabel: string;
  /** Monday of the week the note is about. */
  weekOf: IsoDate;
  /** The family's own words, shown as written. */
  body: string;
}

export const MAX_SEEN_AT_HOME = 6;

export interface OnePagerModel {
  period: Period;
  /** Notes the family shared from home in the period, newest week first. */
  seenAtHome: SeenAtHome[];
  /** False for an older app that syncs no step states. */
  hasSteps: boolean;
  support: SupportSummary;
  practice: PracticeSummary;
  newSince: NewStep[];
  workingOn: WorkingStep[];
  areas: AreaDumbbell[];
}

export interface OnePagerInput {
  stepStates: readonly SkillStepState[];
  sessions: readonly SessionSummary[];
  supportProfile: SupportProfile | null;
  /** Notes visible to the viewer (RLS already limits them to ones shared with them). */
  homeNotes?: readonly HomeNote[];
  period?: Period;
  now?: Date;
  locale?: string;
}

export const MAX_NEW = 6;
export const MAX_WORKING = 5;

const NEW_STAGES: readonly StepStage[] = ["places", "steady", "own"];

function inPeriod(d: IsoDate | null, p: Period): d is IsoDate {
  return d !== null && d >= p.from && d <= p.to;
}

function stepRef(s: SkillStepState, locale: string): StepRef | null {
  const skill = skillById(s.skillId);
  // A skill the portal's registry does not know (a newer app) is left out
  // rather than shown under an invented name.
  if (!skill) return null;
  const def = skill.steps.find((x) => x.n === s.step);
  return {
    skillId: s.skillId,
    skillLabel: localized(skill.label, locale),
    domain: skill.domain,
    step: s.step,
    canDo: localized(def?.text, locale),
    lastPractisedOn: s.lastPractisedOn,
  };
}

function bySkillThenStep(a: StepRef, b: StepRef): number {
  return (
    domainRank(a.domain) - domainRank(b.domain) ||
    skillRank(a.skillId) - skillRank(b.skillId) ||
    a.step - b.step
  );
}

export function supportSummary(profile: SupportProfile | null): SupportSummary {
  if (!profile) return { settings: [], language: null, shared: false };
  const settings = SUPPORT_KEYS.filter((k) => profile[k] === true);
  return { settings, language: profile.language ?? null, shared: true };
}

export function buildOnePager(input: OnePagerInput): OnePagerModel {
  const locale = input.locale ?? "en";
  const period = input.period ?? defaultPeriod(input.now);
  const states = input.stepStates;

  // --- Practice at home -------------------------------------------------------
  const days = new Set<string>();
  const weeks = new Set<string>();
  for (const s of input.sessions) {
    const day = localDay(s.at);
    if (day < period.from || day > period.to) continue;
    days.add(day);
    weeks.add(mondayOf(day));
  }
  const weeksInPeriod = Math.max(1, Math.ceil((daysBetween(period.from, period.to) + 1) / 7));

  let stepsMovedUp = 0;
  let stepsInProgress = 0;
  const newSince: NewStep[] = [];
  const workingOn: WorkingStep[] = [];
  const areaStart = new Map<string, number>();
  const areaEnd = new Map<string, number>();
  const areasSeen = new Set<string>();

  for (const s of states) {
    const ref = stepRef(s, locale);
    if (!ref) continue;
    const dates = effectiveStageDates(s);
    const atEnd = stageOn(s, period.to);
    // The day BEFORE the period starts, so a step reached on its first day counts as new.
    const atStart = stageOn(s, addDays(period.from, -1));

    if (dates.some((d) => inPeriod(d, period))) stepsMovedUp++;
    if (atEnd === "exploring" || atEnd === "help") stepsInProgress++;

    for (const k of NEW_STAGES) {
      const d = dates[STAGES.indexOf(k)];
      if (inPeriod(d, period)) {
        newSince.push({ ...ref, stage: k, reachedOn: d });
        break;
      }
    }

    if (atEnd === "help") {
      workingOn.push({ ...ref, since: dates[STAGES.indexOf("help")] });
    }

    if (atEnd) {
      areasSeen.add(ref.domain);
      if (isOwnOrAbove(atStart)) areaStart.set(ref.domain, (areaStart.get(ref.domain) ?? 0) + 1);
      if (isOwnOrAbove(atEnd)) areaEnd.set(ref.domain, (areaEnd.get(ref.domain) ?? 0) + 1);
    }
  }

  newSince.sort((a, b) => (a.reachedOn < b.reachedOn ? 1 : a.reachedOn > b.reachedOn ? -1 : bySkillThenStep(a, b)));
  workingOn.sort((a, b) => {
    const la = a.lastPractisedOn ?? "";
    const lb = b.lastPractisedOn ?? "";
    return la < lb ? 1 : la > lb ? -1 : bySkillThenStep(a, b);
  });

  const areas: AreaDumbbell[] = [...areasSeen]
    .sort((a, b) => domainRank(a) - domainRank(b))
    .map((domain) => ({ domain, start: areaStart.get(domain) ?? 0, end: areaEnd.get(domain) ?? 0 }));

  const seenAtHome: SeenAtHome[] = (input.homeNotes ?? [])
    // A week that starts before the period but runs into it still counts.
    .filter((n) => n.weekOf <= period.to && addDays(n.weekOf, 6) >= period.from)
    .map((n) => {
      const skill = skillById(n.skillId);
      return {
        id: n.id,
        skillId: n.skillId,
        skillLabel: skill ? localized(skill.label, locale) : n.skillId,
        weekOf: n.weekOf,
        body: n.body,
      };
    })
    .sort((a, b) => (a.weekOf < b.weekOf ? 1 : a.weekOf > b.weekOf ? -1 : a.id.localeCompare(b.id)))
    .slice(0, MAX_SEEN_AT_HOME);

  return {
    period,
    seenAtHome,
    hasSteps: states.some((s) => skillById(s.skillId) && currentStage(s) !== null),
    support: supportSummary(input.supportProfile),
    practice: {
      daysPlayed: days.size,
      activeWeeks: weeks.size,
      weeksInPeriod,
      stepsMovedUp,
      stepsInProgress,
    },
    newSince: newSince.slice(0, MAX_NEW),
    workingOn: workingOn.slice(0, MAX_WORKING),
    areas,
  };
}
